'use client';

import { cn } from '@/lib/cn';
import { useEffect, useState, type VideoHTMLAttributes } from 'react';
import { useViewportPreviewVideo } from '@/components/explore/useViewportPreviewVideo';

type Props = Pick<VideoHTMLAttributes<HTMLVideoElement>, 'aria-label' | 'aria-hidden' | 'onError' | 'onLoadedMetadata' | 'onLoadedData' | 'onCanPlay'> & {
  src?: string | null; poster?: string | null; className?: string; mediaClassName?: string; enabled?: boolean;
};

/** Every autoplay list preview shares the same viewport/page/decoder budget. */
export function ViewportPreviewVideo({ src, poster, className = '', mediaClassName = 'h-full w-full object-cover', enabled = true, onError, ...aria }: Props) {
  const [failed, setFailed] = useState<string>();
  const [ready, setReady] = useState<string>();
  const { containerRef, videoRef, shouldLoadVideo } = useViewportPreviewVideo(src || undefined, Boolean(enabled && src && failed !== src));
  useEffect(() => { if (!shouldLoadVideo) setReady(undefined); }, [shouldLoadVideo]);
  return <span ref={containerRef} className={cn('relative block overflow-hidden', className)} data-viewport-preview>
    {poster && <img src={poster} alt="" loading="lazy" decoding="async" className={mediaClassName} />}
    {shouldLoadVideo && <video {...aria} ref={videoRef} src={src || undefined} poster={poster || undefined}
      className={`absolute inset-0 ${mediaClassName} ${ready === src ? '' : 'opacity-0'}`} muted loop playsInline preload="none" disablePictureInPicture
      onPlaying={() => setReady(src || undefined)} onError={event => { setFailed(src || undefined); onError?.(event); }} />}
  </span>;
}
